import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Mail, User, Tag, Check, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const INTEREST_CATEGORIES = [
  { id: 'conferences', label: 'Conferences', emoji: '🎤' },
  { id: 'marketing', label: 'Marketing', emoji: '📢' },
  { id: 'tech', label: 'Tech', emoji: '💻' },
  { id: 'health', label: 'Health', emoji: '🏥' },
  { id: 'finance', label: 'Finance', emoji: '💰' },
  { id: 'ai', label: 'AI', emoji: '🤖' },
  { id: 'virtual', label: 'Virtual', emoji: '🌐' },
  { id: 'retail', label: 'Retail', emoji: '🛍️' },
  { id: 'manufacturing', label: 'Manufacturing', emoji: '🏭' },
  { id: 'education', label: 'Education', emoji: '📚' },
  { id: 'energy', label: 'Energy', emoji: '⚡' },
  { id: 'pharma', label: 'Pharma', emoji: '💊' },
];

const STORAGE_KEY = 'events_hub_subscribe_modal';
const VIEWS_KEY = 'events_hub_event_views';

export default function SubscribeModal() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [interests, setInterests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Check if user has dismissed or already subscribed
    const modalState = localStorage.getItem(STORAGE_KEY);
    if (modalState === 'dismissed' || modalState === 'subscribed') {
      return;
    }

    // Check event views
    const eventViews = parseInt(localStorage.getItem(VIEWS_KEY) || '0', 10);
    
    // Show modal after 10 seconds OR if user has viewed 2+ events
    if (eventViews >= 2) {
      setIsOpen(true);
    } else {
      const timer = setTimeout(() => {
        const currentState = localStorage.getItem(STORAGE_KEY);
        if (currentState !== 'dismissed' && currentState !== 'subscribed') {
          setIsOpen(true);
        }
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem(STORAGE_KEY, 'dismissed');
  };

  const toggleInterest = (interestId) => {
    setInterests(prev => 
      prev.includes(interestId) 
        ? prev.filter(i => i !== interestId)
        : [...prev, interestId]
    );
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError(t('subscribe.invalidEmail'));
      return;
    }
    setError('');
    setStep(2);
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    setStep(3);
  };

  const handleFinalSubmit = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/subscribe-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name: name || null,
          interests: interests.map(id => 
            INTEREST_CATEGORIES.find(c => c.id === id)?.label || id
          )
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        localStorage.setItem(STORAGE_KEY, 'subscribed');
        
        // Close modal after 3 seconds
        setTimeout(() => {
          setIsOpen(false);
        }, 3000);
      } else {
        setError(data.message || data.detail || t('subscribe.genericError'));
      }
    } catch (err) {
      setError(t('subscribe.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 relative">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Mail className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-bold">{t('subscribe.title')}</h2>
          </div>
          <p className="text-blue-100 text-sm">{t('subscribe.subtitle')}</p>
          
          {/* Progress Indicator */}
          {!success && (
            <div className="flex gap-2 mt-4">
              {[1, 2, 3].map((s) => (
                <div 
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    s <= step ? 'bg-white' : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            /* Success State */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('subscribe.successTitle')}</h3>
              <p className="text-gray-600">{t('subscribe.successDesc')}</p>
            </div>
          ) : step === 1 ? (
            /* Step 1: Email */
            <form onSubmit={handleEmailSubmit}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('subscribe.emailLabel')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('subscribe.emailPlaceholder')}
                  className="pl-10 h-12"
                  autoFocus
                />
              </div>
              {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
              <Button 
                type="submit"
                className="w-full mt-4 h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold"
              >
                {t('subscribe.subscribe')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-xs text-gray-500 text-center mt-4">
                {t('subscribe.noSpam')}
              </p>
            </form>
          ) : step === 2 ? (
            /* Step 2: Name */
            <form onSubmit={handleNameSubmit}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('subscribe.nameLabel')}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('subscribe.namePlaceholder')}
                  className="pl-10 h-12"
                  autoFocus
                />
              </div>
              <Button 
                type="submit"
                className="w-full mt-4 h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold"
              >
                {t('subscribe.continue')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <button 
                type="button"
                onClick={() => setStep(3)}
                className="w-full mt-2 text-sm text-gray-500 hover:text-gray-700"
              >
                {t('subscribe.skipStep')}
              </button>
            </form>
          ) : (
            /* Step 3: Interests */
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Tag className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">{t('subscribe.selectInterests')}</span>
              </div>
              
              <div className="flex flex-wrap gap-2 mb-6">
                {INTEREST_CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => toggleInterest(category.id)}
                    className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
                      interests.includes(category.id)
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="mr-1">{category.emoji}</span>
                    {category.label}
                  </button>
                ))}
              </div>
              
              {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
              
              <Button 
                onClick={handleFinalSubmit}
                disabled={isLoading}
                className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('subscribe.subscribing')}
                  </>
                ) : (
                  <>
                    {t('subscribe.done')}
                    <Check className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              
              <button 
                type="button"
                onClick={handleFinalSubmit}
                disabled={isLoading}
                className="w-full mt-2 text-sm text-gray-500 hover:text-gray-700"
              >
                {t('subscribe.skipDefault')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Utility function to track event views (call this from event detail pages)
export function trackEventView() {
  const currentViews = parseInt(localStorage.getItem(VIEWS_KEY) || '0', 10);
  localStorage.setItem(VIEWS_KEY, String(currentViews + 1));
}
